import { z } from "zod"

/**
 * The payables surface (PRD: engagement-analysis-layer, phase 8). Single
 * definition of the wire shapes: the API routes import these and the
 * api-client parses responses through them, so the two cannot drift.
 *
 * Two decisions show up all over this file.
 *
 * **Every match carries its evidence, never a verdict alone.** A reviewer is
 * accountable for a payment they approve, so the response says what was
 * compared and with which values, and the screen shows that rather than a
 * green tick. `confidence` is a summary of the evidence, not a substitute for
 * it.
 *
 * **Nothing derived is stored, so nothing derived is sent as authoritative.**
 * Lanes, matches and aging are recomputed from the invoices, orders and bank
 * rows on every read. What the server persists, and therefore what the client
 * can change, is only the human decisions under `decisions`.
 */

const datePattern = /^\d{4}-\d{2}-\d{2}$/

export const ApiPayableCheckSchema = z.enum([
  "po_counterparty",
  "po_total",
  "po_currency",
  "po_reference",
  "payment_direction",
  "payment_amount",
  "payment_counterparty",
  "payment_window",
  "payment_currency",
])

export type ApiPayableCheck = z.infer<typeof ApiPayableCheckSchema>

export const ApiPayableEvidenceSchema = z.object({
  check: ApiPayableCheckSchema,
  outcome: z.enum(["passed", "failed", "not_available"]),
  /** What was compared, in words a reviewer reads. */
  label: z.string(),
  expected: z.string().nullable(),
  actual: z.string().nullable(),
})

export type ApiPayableEvidence = z.infer<typeof ApiPayableEvidenceSchema>

export const ApiPayableConfidenceSchema = z.enum(["high", "medium", "low"])

export const ApiPayableRowKeySchema = z.object({
  documentId: z.string().uuid(),
  recordType: z.string(),
  recordIndex: z.number().int(),
})

export type ApiPayableRowKey = z.infer<typeof ApiPayableRowKeySchema>

export const ApiPayablePoCandidateSchema = z.object({
  purchaseOrderDocumentId: z.string().uuid(),
  confidence: ApiPayableConfidenceSchema,
  evidence: z.array(ApiPayableEvidenceSchema),
})

export type ApiPayablePoCandidate = z.infer<typeof ApiPayablePoCandidateSchema>

/**
 * `ambiguous` is not a failure and not a match: more than one order was
 * equally good, so the queue names them all and a person chooses. The machine
 * guessing here is the same mistake the transfer pairer refuses to make.
 */
export const ApiPayablePoResultSchema = z.object({
  kind: z.enum(["skipped", "matched", "ambiguous", "none"]),
  match: ApiPayablePoCandidateSchema.nullable(),
  candidates: z.array(ApiPayablePoCandidateSchema),
  evidence: z.array(ApiPayableEvidenceSchema),
})

export type ApiPayablePoResult = z.infer<typeof ApiPayablePoResultSchema>

export const ApiPayablePaymentCandidateSchema = z.object({
  row: ApiPayableRowKeySchema,
  confidence: ApiPayableConfidenceSchema,
  evidence: z.array(ApiPayableEvidenceSchema),
})

export type ApiPayablePaymentCandidate = z.infer<
  typeof ApiPayablePaymentCandidateSchema
>

/**
 * `none` means unpaid, which is pending and not an exception. `near_miss` and
 * `refund_suspected` are exceptions, because in both cases something was
 * found and it did not hold up.
 */
export const ApiPayablePaymentResultSchema = z.object({
  kind: z.enum([
    "skipped",
    "matched",
    "ambiguous",
    "near_miss",
    "refund_suspected",
    "none",
  ]),
  match: ApiPayablePaymentCandidateSchema.nullable(),
  candidates: z.array(ApiPayablePaymentCandidateSchema),
  evidence: z.array(ApiPayableEvidenceSchema),
})

export type ApiPayablePaymentResult = z.infer<
  typeof ApiPayablePaymentResultSchema
>

export const ApiPayableLaneSchema = z.enum([
  "needs_review",
  "exceptions",
  "approved",
])

export type ApiPayableLane = z.infer<typeof ApiPayableLaneSchema>

export const ApiPayableAgingBucketSchema = z.enum(["0_30", "31_60", "60_plus"])

export type ApiPayableAgingBucket = z.infer<typeof ApiPayableAgingBucketSchema>

/** What a person has decided about this invoice, and nothing else. */
export const ApiPayableDecisionsSchema = z.object({
  exceptionsAcknowledgedAt: z.string().nullable(),
  note: z.string().nullable(),
  confirmedPurchaseOrderDocumentId: z.string().uuid().nullable(),
  dismissedPurchaseOrderDocumentIds: z.array(z.string().uuid()),
  confirmedPaymentRow: ApiPayableRowKeySchema.nullable(),
  dismissedPaymentRows: z.array(ApiPayableRowKeySchema),
})

export type ApiPayableDecisions = z.infer<typeof ApiPayableDecisionsSchema>

export const ApiPayableSchema = z.object({
  documentId: z.string().uuid(),
  title: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  counterpartyKey: z.string().nullable(),
  documentNumber: z.string().nullable(),
  invoiceDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  totalCents: z.number().int().nullable(),
  currency: z.string().nullable(),
  /** The document's own state, so the queue never re-invents approval. */
  approved: z.boolean(),
  /** True when open required review flags would refuse an approval. */
  blockedByFlags: z.boolean(),
  lane: ApiPayableLaneSchema,
  failedChecks: z.array(
    z.object({ check: ApiPayableCheckSchema, label: z.string() })
  ),
  aging: ApiPayableAgingBucketSchema.nullable(),
  overdue: z.boolean(),
  po: ApiPayablePoResultSchema,
  payment: ApiPayablePaymentResultSchema,
  decisions: ApiPayableDecisionsSchema,
})

export type ApiPayable = z.infer<typeof ApiPayableSchema>

/** Every order any match or candidate names, so the screen needs no second read. */
export const ApiPayableOrderRefSchema = z.object({
  documentId: z.string().uuid(),
  title: z.string().nullable(),
  documentNumber: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  orderDate: z.string().nullable(),
  totalCents: z.number().int().nullable(),
  currency: z.string().nullable(),
})

export type ApiPayableOrderRef = z.infer<typeof ApiPayableOrderRefSchema>

export const ApiPayablePaymentRefSchema = z.object({
  row: ApiPayableRowKeySchema,
  recordDate: z.string().nullable(),
  description: z.string().nullable(),
  /** Signed as stored on the statement. */
  amountCents: z.number().int().nullable(),
  /** Signed as flow: negative is money leaving, after the polarity verdict. */
  flowCents: z.number().int().nullable(),
  accountId: z.string().uuid().nullable(),
  currency: z.string().nullable(),
})

export type ApiPayablePaymentRef = z.infer<typeof ApiPayablePaymentRefSchema>

export const ApiPayableDuplicateSchema = z.object({
  counterpartyKey: z.string().nullable(),
  totalCents: z.number().int(),
  documentIds: z.array(z.string().uuid()),
})

export type ApiPayableDuplicate = z.infer<typeof ApiPayableDuplicateSchema>

export const ApiPayableVendorSchema = z.object({
  counterpartyKey: z.string().nullable(),
  label: z.string(),
  spendCents: z.number().int(),
  invoiceCount: z.number().int(),
  averageCents: z.number().int(),
})

export type ApiPayableVendor = z.infer<typeof ApiPayableVendorSchema>

export const ApiPayableSettingsSchema = z.object({
  /** No purchase orders in this engagement: a missing one is not an exception. */
  ignorePoMatching: z.boolean(),
  /** No bank statements for the paying account: stop looking for the payment. */
  ignorePaymentMatching: z.boolean(),
})

export type ApiPayableSettings = z.infer<typeof ApiPayableSettingsSchema>

export const ApiPayablesResponseSchema = z.object({
  /** The date the aging and overdue counts were computed against. */
  asOf: z.string().regex(datePattern),
  settings: ApiPayableSettingsSchema,
  metrics: z.object({
    needsReviewCount: z.number().int(),
    exceptionCount: z.number().int(),
    overdueCount: z.number().int(),
    approvedCount: z.number().int(),
    /** Everything not yet approved, as money. */
    totalPendingCents: z.number().int(),
  }),
  aging: z.array(
    z.object({
      bucket: ApiPayableAgingBucketSchema,
      invoiceCount: z.number().int(),
      totalCents: z.number().int(),
    })
  ),
  topVendors: z.array(ApiPayableVendorSchema),
  duplicates: z.array(ApiPayableDuplicateSchema),
  payables: z.array(ApiPayableSchema),
  orders: z.array(ApiPayableOrderRefSchema),
  paymentRows: z.array(ApiPayablePaymentRefSchema),
  /**
   * Invoices left out of every number above because extraction has not given
   * them a total yet. Reported rather than dropped: a queue that silently
   * ignored an invoice would be a queue an accountant cannot trust.
   *
   * Named as well as counted, so "Show them" can actually show them. A count
   * on its own tells a reviewer something is missing without telling them
   * which document to open, which is a gap report nobody can act on.
   */
  unreadableCount: z.number().int(),
  unreadable: z.array(
    z.object({
      documentId: z.string().uuid(),
      title: z.string().nullable(),
      counterpartyName: z.string().nullable(),
      documentNumber: z.string().nullable(),
      invoiceDate: z.string().nullable(),
    })
  ),
})

export type ApiPayablesResponse = z.infer<typeof ApiPayablesResponseSchema>

export const ApiPayableDecisionBodySchema = z
  .object({
    /**
     * Accept the invoice with its checks still failing, or withdraw that
     * acceptance. The failing checks stay visible either way.
     */
    acknowledgeExceptions: z.boolean().optional(),
    note: z.string().max(1000).nullable().optional(),
    confirmPurchaseOrderDocumentId: z.string().uuid().nullable().optional(),
    dismissPurchaseOrderDocumentId: z.string().uuid().optional(),
    restorePurchaseOrderDocumentId: z.string().uuid().optional(),
    confirmPaymentRow: ApiPayableRowKeySchema.nullable().optional(),
    dismissPaymentRow: ApiPayableRowKeySchema.optional(),
    restorePaymentRow: ApiPayableRowKeySchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: "A decision must change something.",
  })

export type ApiPayableDecisionBody = z.infer<
  typeof ApiPayableDecisionBodySchema
>

export const ApiPayableDecisionResponseSchema = z.object({
  decisions: ApiPayableDecisionsSchema,
})

export type ApiPayableDecisionResponse = z.infer<
  typeof ApiPayableDecisionResponseSchema
>

/**
 * There is deliberately no bulk-approve shape here.
 *
 * "Approve 3 matched" on the payables screen is the same act as approving
 * three documents in review, and `POST /v1/documents/bulk-approvals` already
 * does exactly that: an explicit id list, an optional projection hash per
 * entry, one transaction per document so one blocked invoice cannot undo the
 * approvals that already landed, and a per-document result that names
 * `open_required_flags` when review flags refuse the approval. A payables
 * copy would be a second approval concept to keep in step, plus a second
 * event for the workflow engine to learn.
 */
